import React, { startTransition } from "react";
import { useQueryLoader } from "react-relay";
import { useParams, useNavigate } from "react-router-dom";
import { Tab, Tabs, Typography, styled } from "@mui/material";
import TabPanel from "../../components/common/TabPanel";
import PageContainer from "../../components/common/PageContainer";
import TabContainer from "../../components/common/TabContainer";
import PredecessorsTab, { hasPredecessors, predecessorsTabQuery } from "../../components/common/PredecessorsTab";

import { getToshiType, OPENQUAKE_HAZARD_SOLUTION_ROUTE } from "../../utils/routeUtils";
import { PredecessorsTabQuery } from "../../components/common/__generated__/PredecessorsTabQuery.graphql";
import { OpenquakeHazardSolutionDetailTabQuery } from "./__generated__/OpenquakeHazardSolutionDetailTabQuery.graphql";
import OpenquakeHazardSolutionDetailTab, {
  openquakeHazardSolutionDetailTabQuery,
} from "./OpenquakeHazardSolutionDetailTab";
import CentredCircularProgress from "../../components/common/CentredCircularProgress";

const StyledTab = styled(Tab)(({ theme }) => ({
  maxWidth: "5vw",
  borderRight: `2px solid ${theme.palette.divider}`,
}));

type OpenquakeHazardSolutionParams = {
  id: string;
  tab: string;
};

const OpenquakeHazardSolution: React.FC = () => {
  const { id, tab } = useParams<OpenquakeHazardSolutionParams>();
  const navigate = useNavigate();
  const [hazardSolQueryRef, loadHazardSolQuery] = useQueryLoader<OpenquakeHazardSolutionDetailTabQuery>(
    openquakeHazardSolutionDetailTabQuery,
  );
  const [predQueryRef, loadPredQuery] = useQueryLoader<PredecessorsTabQuery>(predecessorsTabQuery);
  const typename = getToshiType(id);

  React.useEffect(() => {
    if (!id) {
      return;
    }
    switch (tab) {
      case "PredecessorsTab":
        startTransition(() => loadPredQuery({ id }, { fetchPolicy: "network-only" }));
        break;
      default:
        startTransition(() => loadHazardSolQuery({ id }));
    }
  }, [id, loadHazardSolQuery, loadPredQuery, tab]);

  const renderTab = () => {
    switch (tab) {
      case "PredecessorsTab":
        return (
          <TabPanel>
            <React.Suspense fallback={<CentredCircularProgress />}>
              {predQueryRef && <PredecessorsTab queryRef={predQueryRef} />}
            </React.Suspense>
          </TabPanel>
        );
      default:
        return (
          <TabPanel>{hazardSolQueryRef && <OpenquakeHazardSolutionDetailTab queryRef={hazardSolQueryRef} />}</TabPanel>
        );
    }
  };

  if (typename !== "OpenquakeHazardSolution") {
    return (
      <Typography variant="h5" gutterBottom>
        Not an OpenquakeHazardSolution
      </Typography>
    );
  }

  return (
    <PageContainer maxWidth="xl">
      <Typography variant="h5" gutterBottom>
        OpenquakeHazardSolution: {id}&nbsp;
      </Typography>
      <TabContainer maxWidth="xl">
        <Tabs
          orientation="vertical"
          value={tab ?? "DetailsTab"}
          onChange={(_e, val) => {
            navigate(`/${OPENQUAKE_HAZARD_SOLUTION_ROUTE}/${id}/${val}`);
          }}
        >
          <StyledTab label="Details" id="DetailsTab" value={"DetailsTab"} />
          {hasPredecessors(typename) && <StyledTab label="Parents" id="PredecessorsTab" value="PredecessorsTab" />}
        </Tabs>
        {renderTab()}
      </TabContainer>
    </PageContainer>
  );
};

export default OpenquakeHazardSolution;
